<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import type { ArticleAction } from '@/common/bot/types';
import PlsButton1 from '@/components/PlsButton1.vue';
import { useBotUser } from '@/stores/useBotUser';
import { useI18n } from '@/plugins/i18n';
import {
  useArticleBackButtonCustomisation,
  useArticleContactOptionsButtonCustomisation,
  useArticleActionToBeTakenButtonCustomisation,
} from '../composables/useArticleButtonCustomisation';

interface Props {
  articleAction?: ArticleAction | null;
  canConnectToSupport: boolean;
  enableMetadataRules: boolean;
  isAiFlowEnabled: boolean;
}

const props = defineProps<Props>();

const { t } = useI18n();
const { user } = storeToRefs(useBotUser());
const isLoggedIn = computed(() => !user.value.isGuest);

// Customisation
const backButtonCustomisation = useArticleBackButtonCustomisation();
const contactOptionsButtonCustomisation = useArticleContactOptionsButtonCustomisation();
const actionButtonCustomisation = useArticleActionToBeTakenButtonCustomisation();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'action'): void;
  (e: 'connect'): void;
}>();

interface ButtonConfig {
  id: string;
  text: string;
  onClick: () => void;
  customisation?: {
    textColor?: string;
    backgroundColor?: string;
    borderColor?: string;
  };
}

type CustomisationSource = {
  buttonText?: { value: string };
  buttonTextColor?: { value: string };
  buttonBackgroundColor?: { value: string };
  buttonBorderColor?: { value: string };
};

const createButtonCustomisation = (source: CustomisationSource) => ({
  textColor: source.buttonTextColor?.value,
  backgroundColor: source.buttonBackgroundColor?.value,
  borderColor: source.buttonBorderColor?.value,
});

const createBackButton = (): ButtonConfig => ({
  id: 'back',
  text: backButtonCustomisation.buttonText.value,
  onClick: () => emit('back'),
  customisation: createButtonCustomisation(backButtonCustomisation),
});

const createContactButton = (): ButtonConfig => {
  const { canConnectToSupport } = props;
  return {
    id: 'need-more-help-btn',
    text:
      contactOptionsButtonCustomisation.buttonText.value ||
      (canConnectToSupport ? t('chat.sys.navigation.contact_support') : t('article.feedback.button.need_more_help')),
    onClick: () => emit('connect'),
    customisation: createButtonCustomisation(contactOptionsButtonCustomisation),
  };
};

const createActionButton = (): ButtonConfig => ({
  id: `article-action-btn-${props.articleAction?.action}`,
  text: props.articleAction?.actionTitle || '',
  onClick: () => emit('action'),
  customisation: createButtonCustomisation(actionButtonCustomisation),
});

const shouldShowContactButton = computed(() => {
  const { canConnectToSupport, enableMetadataRules } = props;
  return canConnectToSupport || (enableMetadataRules && isLoggedIn.value);
});

const buttons = computed<ButtonConfig[]>(() => {
  const { articleAction, canConnectToSupport, enableMetadataRules, isAiFlowEnabled } = props;

  if (isAiFlowEnabled) {
    return [];
  }

  const buttonList: ButtonConfig[] = [createBackButton()];

  if (!canConnectToSupport && !articleAction && !enableMetadataRules) {
    return buttonList;
  }

  if (shouldShowContactButton.value) {
    buttonList.push(createContactButton());
  } else if (articleAction) {
    buttonList.push(createActionButton());
  }

  return buttonList;
});
</script>

<template>
  <div>
    <div class="buttons-container">
      <PlsButton1
        v-for="button in buttons"
        :key="button.id"
        :text="button.text"
        :text-color="button.customisation?.textColor"
        :background-color="button.customisation?.backgroundColor"
        :border-color="button.customisation?.borderColor"
        @click="button.onClick"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.buttons-container {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-top: 8px;
}
</style>
